import "./PaymentHistoryModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { useForm } from "react-hook-form";

import type { CodeType } from "./CodeSection";
import CodeSection from "./CodeSection";

export type ReportedDiagnosisCodesFormData = {
  reported_diagnosis_codes: CodeType[];
};

type ReportedDiagnosisCodesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReportedDiagnosisCodesFormData) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  initialData?: CodeType[];
  title: string;
};

const FormErrors = ({
  errors,
}: {
  errors: FieldErrors<ReportedDiagnosisCodesFormData>;
}) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const ReportedDiagnosisCodesModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
}: ReportedDiagnosisCodesModalProps) => {
  const [diagnosisCodes, setDiagnosisCodes] = useState<CodeType[]>([]);

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ReportedDiagnosisCodesFormData>({
    defaultValues: {
      reported_diagnosis_codes: [],
    },
    mode: "onBlur",
    shouldFocusError: false,
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setDiagnosisCodes(initialData);
        reset({
          reported_diagnosis_codes: initialData,
        });
      } else {
        setDiagnosisCodes([]);
        reset({
          reported_diagnosis_codes: [],
        });
      }
    }
  }, [isOpen, initialData, reset, setDiagnosisCodes]);

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await onSubmit({
        ...data,
        reported_diagnosis_codes: diagnosisCodes,
      });
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  });

  const handleAddDiagnosisCode = () => {
    setDiagnosisCodes([
      ...diagnosisCodes,
      { code: "", description: "", indicator: "0" },
    ]);
  };

  const handleRemoveDiagnosisCode = (index: number) => {
    const updatedCodes = [...diagnosisCodes];
    updatedCodes.splice(index, 1);
    setDiagnosisCodes(updatedCodes);
  };

  const handleDiagnosisCodeChange = (
    index: number,
    updates: Partial<CodeType>,
  ) => {
    const updatedCodes = diagnosisCodes.map((code, i) =>
      i === index ? { ...code, ...updates } : code,
    );
    setDiagnosisCodes(updatedCodes);
  };

  return (
    <>
      <KaboodleModal
        isOpen={isOpen}
        onClose={onClose}
        footer={
          <div className="flex jc-c">
            <Button onClick={handleFormSubmit}>Save</Button>
            <Button onClick={onClose}>Cancel</Button>
          </div>
        }
      >
        <Card heading={title}>
          <FormErrors errors={errors} />
          <CodeSection
            codes={diagnosisCodes}
            type="ICD"
            onAdd={handleAddDiagnosisCode}
            onRemove={handleRemoveDiagnosisCode}
            onChange={handleDiagnosisCodeChange}
          />
          <div className="border mrg-bottom"></div>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default ReportedDiagnosisCodesModal;
